/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../store/actions/auth";

// Default inactivity timeout: 30 minutes (in milliseconds).
const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

// User activity events that reset the idle timer.
const ACTIVITY_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];

/**
 * Automatically dispatches logout after a period of user inactivity.
 * Mount this hook only for authenticated users, or pass enabled=false to disable it.
 *
 * @param {number} [timeoutMs] - Inactivity threshold in milliseconds. Defaults to 30 minutes.
 * @param {boolean} [enabled] - Whether the timer is active. Defaults to true.
 */
const useIdleTimeout = (timeoutMs = DEFAULT_IDLE_TIMEOUT_MS, enabled = true) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        dispatch(logout());
      }, timeoutMs);
    };

    // Start the timer immediately on mount.
    resetTimer();

    // Reset the timer on any detected user activity.
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [dispatch, timeoutMs, enabled]);
};

export default useIdleTimeout;
